import { useState, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { WorkspacePermission } from 'src/redux/workspaces/constants';
import { getCurrentUser } from 'src/selectors/user-selectors';
import urls from 'src/urls';

type WorkspaceAdminStatusHook = {
  isWorkspaceAdmin: boolean;
  hasError: boolean;
};

export const useWorkspaceAdminStatus = (
  workspaceUuid: string | null
): WorkspaceAdminStatusHook => {
  const [hasError, setHasError] = useState(false);
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const currentUserUuid = currentUser?.uuid;

  useEffect(
    () => {
      // useEffect does not accept async functions directly.
      (async () => {
        try {
          if (workspaceUuid && currentUserUuid) {
            const url = urls.api.v20.workspacePermissionsForUser(
              workspaceUuid,
              currentUserUuid
            );

            const resp = await fetch(url);
            if (resp.ok) {
              const { values } = (await resp.json()) as {
                values: { permission: WorkspacePermission }[];
              };
              setIsWorkspaceAdmin(
                values[0]?.permission === WorkspacePermission.Owner
              );
            } else {
              // workspacePermissionsForUser is only accessible by workspace admins. This
              // means that a 403 is returned for non-admin users instead of a 200 with the
              // user's permissions. In this case isWorkspaceAdmin = hasError = false. We use it
              // in favor of userPermissionsForWorkspace as it is scoped to a single workspace.
              if (resp.status === 403) {
                setIsWorkspaceAdmin(false);
              } else {
                setHasError(true);
              }
            }
          }
        } catch {
          setHasError(true);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserUuid, workspaceUuid]
  );
  return { isWorkspaceAdmin, hasError };
};
